import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Grid,
  Box,
  Flex,
  Heading,
  Text,
  Divider,
  Switch,
  Stack,
  useMediaQuery,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import OwnSelect, { SelectedValues } from 'components/OwnSelect';

import { featureFlags } from 'pages/Plan/featureFlags';

import { Capability } from 'types/access-control';
import { Base } from 'types/base';

type FormErrors = {
  states?: boolean;
  description?: boolean;
  externalId?: boolean;
  numberOfUsers?: boolean;
  integrations?: boolean;
};

type CapabilityPostPayload = Array<{
  key: string;
  value: number[] | number | string | string[];
}>;

type CapabilityPutPayload = Array<{
  id: number;
  value: number[] | number | string | string[];
}>;

type PlanFormValues = {
  description: string;
  externalId: string;
  numberOfUsers: number;
  baseSelected: number[];
  integrationSelected: string[];
  useFlow: boolean;
  useFlowDashboard: boolean;
  useParametricBudget: boolean;
  useTemplate: boolean;
  useOpportunities: boolean;
};

interface ModalPlanProps extends ModalRootProps {
  data: {
    plan_id?: number;
    description?: string;
    external_id?: string;
    bases?: number[];
    use_bases_capability_id?: number;
    use_flow_capability_id?: number;
    use_dashboard_flow_capability_id?: number;
    use_opportunities_capability_id?: number;
    use_template_capability_id?: number;
    use_parametric_budget_capability_id?: number;
    number_of_users?: number;
    number_of_users_capability_id?: number;
    integrations: Capability[];
  };
}

function getMissingElements(arr1: string[], arr2: string[]): string[] {
  return arr1.filter((element) => !arr2.includes(element));
}

function parseCapabilityId(id?: number): number | null {
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

const SIENGE_PLATFORM_INTEGRATION_KEY = 'sienge_plataform';
const GESTOR_OBRAS_INTEGRATION_KEY = 'sienge_go';

function hasExclusiveIntegrationConflict(integrations: string[]): boolean {
  return (
    integrations.includes(SIENGE_PLATFORM_INTEGRATION_KEY) &&
    integrations.includes(GESTOR_OBRAS_INTEGRATION_KEY)
  );
}

function validatePlanForm({
  description,
  externalId,
  numberOfUsers,
  baseSelected,
  integrationSelected,
}: Pick<
  PlanFormValues,
  | 'description'
  | 'externalId'
  | 'numberOfUsers'
  | 'baseSelected'
  | 'integrationSelected'
>): FormErrors {
  const formErrors: FormErrors = {};

  if (baseSelected.length === 0) {
    formErrors.states = true;
  }

  if (!description.trim()) {
    formErrors.description = true;
  }

  if (!externalId.trim()) {
    formErrors.externalId = true;
  }

  if (!numberOfUsers || numberOfUsers <= 0) {
    formErrors.numberOfUsers = true;
  }

  if (hasExclusiveIntegrationConflict(integrationSelected)) {
    formErrors.integrations = true;
  }

  return formErrors;
}

function buildCreateCapabilityPayload(
  values: PlanFormValues,
): CapabilityPostPayload {
  const payload: CapabilityPostPayload = [
    {
      key: 'use_bases',
      value: values.baseSelected.map(String),
    },
  ];

  values.integrationSelected.forEach((integration) => {
    payload.push({
      key: integration,
      value: 1,
    });
  });

  if (values.useFlow) {
    payload.push({
      key: 'use_flow',
      value: 1,
    });
  }

  if (values.useFlowDashboard) {
    payload.push({
      key: 'use_dashboard_flow',
      value: 1,
    });
  }

  if (values.useParametricBudget) {
    payload.push({
      key: 'use_parametric_budget',
      value: 1,
    });
  }

  if (values.useTemplate) {
    payload.push({
      key: 'use_template',
      value: 1,
    });
  }

  if (shouldShowUseOpportunitiesFeature && values.useOpportunities) {
    payload.push({
      key: 'use_opportunity_funnel',
      value: 1,
    });
  }

  payload.push({
    key: 'limit_users_plan',
    value: Number(values.numberOfUsers),
  });

  return payload;
}

function buildIntegrationCapabilityIdMap(
  integrations: Capability[],
): Record<string, number> {
  return integrations.reduce((acc, integration) => {
    acc[integration.capability_key] = integration.capability_id;
    return acc;
  }, {} as Record<string, number>);
}

const modalMargin = 120;
const modalMobileFix = 100;
const shouldShowUseOpportunitiesFeature =
  featureFlags.enableOpportunitiesConfiguration;

const ModalPlanAdd: React.FC<ModalPlanProps> = ({
  onConfirm,
  handleClose,
  data,
  mode,
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [description, setDescription] = useState(data.description || '');
  const [externalId, setExternalId] = useState(data.external_id || '');
  const [numberOfUsers, setNumberOfUsers] = useState(data.number_of_users || 0);

  const descriptionRef = React.useRef<HTMLInputElement>(null);
  const externalIdRef = React.useRef<HTMLInputElement>(null);
  const numberOfUsersRef = React.useRef<HTMLInputElement>(null);

  const [isAllBases, setIsAllBases] = useState(false);
  const [baseSelected, setBaseSelected] = useState<number[]>(data.bases || []);
  const [baseList, setBaseList] = useState<Base[]>([]);

  const [integrationSelected, setIntegrationSelected] = useState<string[]>(
    data.integrations?.map((i) => i.capability_key) || [],
  );

  const integrationList = useMemo(
    () => [
      {
        key: SIENGE_PLATFORM_INTEGRATION_KEY,
        name: 'Sienge Plataforma',
      },
      {
        key: GESTOR_OBRAS_INTEGRATION_KEY,
        name: 'Gestor Obras',
      },
      {
        key: 'q_express',
        name: 'QExpress',
      },
    ],
    [],
  );

  const [useFlow, setUseFlow] = useState(
    !!data.use_flow_capability_id || false,
  );

  const [useFlowDashboard, setUseFlowDashboard] = useState(
    !!data.use_dashboard_flow_capability_id || false,
  );

  const [useParametricBudget, setUseParametricBudget] = useState(
    !!data.use_parametric_budget_capability_id || false,
  );

  const [useTemplate, setUseTemplate] = useState(
    !!data.use_template_capability_id || false,
  );

  const [useOpportunities, setUseOpportunities] = useState(
    !!data.use_opportunities_capability_id || false,
  );

  const [loading, setLoading] = useState(false);

  const [isFindingBases, setIsFindingBases] = useState(false);

  const [errors, setErrors] = useState<FormErrors | undefined>();

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.focus();
    }
  }, []);

  const basesToSelect = useMemo(
    () =>
      baseList.map((base) => ({
        value: base.id,
        label: base.description,
      })),
    [baseList],
  );

  const integrationsToSelect = useMemo(
    () =>
      integrationList.map((integration) => ({
        value: integration.key,
        label: integration.name,
      })),
    [integrationList],
  );

  const hasSelectedExclusiveIntegration = useMemo(
    () =>
      integrationSelected.includes(SIENGE_PLATFORM_INTEGRATION_KEY) ||
      integrationSelected.includes(GESTOR_OBRAS_INTEGRATION_KEY),
    [integrationSelected],
  );

  const integrationConflictMessage = `Sienge Plataforma e Gestor Obras são integrações mutuamente exclusivas.`;

  const getBases = useCallback(async () => {
    setIsFindingBases(true);
    try {
      const { data: responseStates } = await api.get(`/base`, {
        params: {
          sort: 'description',
          'filter[no_organization]': '.',
        },
      });
      const states = responseStates.data;
      setBaseList(states);
    } catch (e) {
      toast({
        description: 'Houve um erro ao tentar carregar a lista de estados.',
        status: 'error',
      });
    } finally {
      setIsFindingBases(false);
    }
  }, []);

  useEffect(() => {
    getBases();
  }, [getBases]);

  useEffect(() => {
    if (
      basesToSelect.length > 0 &&
      baseSelected.length === basesToSelect.length
    ) {
      setIsAllBases(true);
    } else {
      setIsAllBases(false);
    }
  }, [baseSelected, basesToSelect]);

  const getFormValues = useCallback(
    (): PlanFormValues => ({
      description,
      externalId,
      numberOfUsers,
      baseSelected,
      integrationSelected,
      useFlow,
      useFlowDashboard,
      useParametricBudget,
      useTemplate,
      useOpportunities,
    }),
    [
      description,
      externalId,
      numberOfUsers,
      baseSelected,
      integrationSelected,
      useFlow,
      useFlowDashboard,
      useParametricBudget,
      useTemplate,
      useOpportunities,
    ],
  );

  const createPlanWithCapabilities = useCallback(
    async (values: PlanFormValues): Promise<void> => {
      const {
        data: {
          data: { id: planId },
        },
      } = await api.post(`/plan`, {
        type: 'manual',
        external_id: values.externalId.trim(),
        description: values.description.trim(),
      });

      const payload = buildCreateCapabilityPayload(values);

      await api.post(`/admin/capability`, {
        object_type: 'plan',
        object_id: planId,
        data: payload,
      });
    },
    [],
  );

  const editPlanWithCapabilities = useCallback(
    async (values: PlanFormValues): Promise<void> => {
      const planId = data.plan_id;
      if (!planId) {
        throw new Error('Missing plan_id for edit mode');
      }

      await api.put(`/plan/${planId}`, {
        type: 'manual',
        external_id: values.externalId.trim(),
        description: values.description.trim(),
      });

      const existingIntegrationKeys = data.integrations.map(
        (integration) => integration.capability_key,
      );

      const integrationCapabilityIdMap = buildIntegrationCapabilityIdMap(
        data.integrations,
      );

      const integrationsToRemove = getMissingElements(
        existingIntegrationKeys,
        values.integrationSelected,
      );

      const integrationsToAdd = getMissingElements(
        values.integrationSelected,
        existingIntegrationKeys,
      );

      const capabilitiesToDelete: number[] = [];

      integrationsToRemove.forEach((integrationKey) => {
        const capabilityId = integrationCapabilityIdMap[integrationKey];
        if (capabilityId) {
          capabilitiesToDelete.push(capabilityId);
        }
      });

      const flowCapabilityId = parseCapabilityId(data.use_flow_capability_id);
      if (flowCapabilityId && !values.useFlow) {
        capabilitiesToDelete.push(flowCapabilityId);
      }

      const flowDashboardCapabilityId = parseCapabilityId(
        data.use_dashboard_flow_capability_id,
      );
      if (flowDashboardCapabilityId && !values.useFlowDashboard) {
        capabilitiesToDelete.push(flowDashboardCapabilityId);
      }

      const parametricBudgetCapabilityId = parseCapabilityId(
        data.use_parametric_budget_capability_id,
      );
      if (parametricBudgetCapabilityId && !values.useParametricBudget) {
        capabilitiesToDelete.push(parametricBudgetCapabilityId);
      }

      const templateCapabilityId = parseCapabilityId(
        data.use_template_capability_id,
      );
      if (templateCapabilityId && !values.useTemplate) {
        capabilitiesToDelete.push(templateCapabilityId);
      }

      const opportunitiesCapabilityId = parseCapabilityId(
        data.use_opportunities_capability_id,
      );
      if (
        shouldShowUseOpportunitiesFeature &&
        opportunitiesCapabilityId &&
        !values.useOpportunities
      ) {
        capabilitiesToDelete.push(opportunitiesCapabilityId);
      }

      if (capabilitiesToDelete.length > 0) {
        await Promise.all(
          capabilitiesToDelete.map((capabilityId) =>
            api.delete(`/admin/capability/${capabilityId}`, {
              params: {
                object_type: 'plan',
                object_id: planId,
              },
            }),
          ),
        );
      }

      const postPayload: CapabilityPostPayload = integrationsToAdd.map(
        (integrationKey) => ({
          key: integrationKey,
          value: 1,
        }),
      );
      const putPayload: CapabilityPutPayload = [];

      const basesCapabilityId = parseCapabilityId(data.use_bases_capability_id);
      if (basesCapabilityId) {
        putPayload.push({
          id: basesCapabilityId,
          value: values.baseSelected.map(String),
        });
      } else {
        postPayload.push({
          key: 'use_bases',
          value: values.baseSelected.map(String),
        });
      }

      const numberOfUsersCapabilityId = parseCapabilityId(
        data.number_of_users_capability_id,
      );
      if (numberOfUsersCapabilityId) {
        putPayload.push({
          id: numberOfUsersCapabilityId,
          value: Number(values.numberOfUsers),
        });
      } else {
        postPayload.push({
          key: 'limit_users_plan',
          value: Number(values.numberOfUsers),
        });
      }

      if (!flowCapabilityId && values.useFlow) {
        postPayload.push({
          key: 'use_flow',
          value: 1,
        });
      }

      if (!flowDashboardCapabilityId && values.useFlowDashboard) {
        postPayload.push({
          key: 'use_dashboard_flow',
          value: 1,
        });
      }

      if (!parametricBudgetCapabilityId && values.useParametricBudget) {
        postPayload.push({
          key: 'use_parametric_budget',
          value: 1,
        });
      }

      if (!templateCapabilityId && values.useTemplate) {
        postPayload.push({
          key: 'use_template',
          value: 1,
        });
      }

      if (
        shouldShowUseOpportunitiesFeature &&
        !opportunitiesCapabilityId &&
        values.useOpportunities
      ) {
        postPayload.push({
          key: 'use_opportunity_funnel',
          value: 1,
        });
      }

      if (postPayload.length > 0) {
        await api.post(`/admin/capability`, {
          object_type: 'plan',
          object_id: planId,
          data: postPayload,
        });
      }

      if (putPayload.length > 0) {
        await api.put(`/admin/capability`, {
          object_type: 'plan',
          object_id: planId,
          data: putPayload,
        });
      }
    },
    [data],
  );

  const handleConfirmSuccess = useCallback((): void => {
    if (onConfirm) {
      onConfirm();
    }

    toast({
      description:
        mode === 'add'
          ? 'Plano adicionado com sucesso!'
          : 'Plano editado com sucesso!',
      status: 'success',
    });

    handleClose();
  }, [handleClose, mode, onConfirm]);

  const handleConfirmError = useCallback(
    (err?: {
      response?: {
        data?: {
          message?: string;
        };
      };
    }): void => {
      const fallbackMessage =
        mode === 'add'
          ? 'Houve um erro ao adicionar o plano!'
          : 'Houve um erro ao editar o plano!';

      const errorMessage = err?.response?.data?.message || fallbackMessage;

      toast({
        description: errorMessage,
        status: 'error',
      });
    },
    [mode],
  );

  const handleConfirm = async (): Promise<void> => {
    if (loading) {
      return;
    }

    const values = getFormValues();
    const formErrors = validatePlanForm(values);

    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);

      if (mode === 'add') {
        await createPlanWithCapabilities(values);
      } else if (mode === 'edit') {
        await editPlanWithCapabilities(values);
      }

      handleConfirmSuccess();
    } catch (err) {
      handleConfirmError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>
          {mode === 'add' ? 'Adicionar plano' : 'Editar plano'}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pb={8}>
          <Grid templateColumns={{ base: '1fr', lg: '1.45fr 1fr' }} gap={6}>
            <Box
            // bg="gray.50"
            // border="1px solid"
            // borderColor="gray.200"
            // borderRadius="xl"
            // p={{ base: 4, md: 5 }}
            >
              <Heading size="sm" color="gray.700" mb={1}>
                Dados cadastrais
              </Heading>
              <Text fontSize="sm" color="gray.500" mb={5}>
                Preencha as informações principais do plano.
              </Text>

              <FormControl isInvalid={!!errors?.description}>
                <FormLabel>Nome do plano</FormLabel>

                <Input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && externalIdRef.current?.focus()
                  }
                  ref={descriptionRef}
                  bg="white"
                  borderColor="gray.300"
                  size="md"
                />

                <FormErrorMessage>
                  {!!errors?.description && 'Nome do plano é obrigatório'}
                </FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors?.externalId}>
                <FormLabel>ID Externo</FormLabel>

                <Input
                  type="text"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && numberOfUsersRef.current?.focus()
                  }
                  ref={externalIdRef}
                  bg="white"
                  borderColor="gray.300"
                  size="md"
                />

                <FormErrorMessage>
                  {!!errors?.externalId && 'ID Externo é obrigatório'}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors?.states} mt={4}>
                <Flex align="center" justify="space-between" mb={2}>
                  <FormLabel m={0}>Bases</FormLabel>
                  <Checkbox
                    isChecked={isAllBases}
                    onChange={(e) => {
                      setIsAllBases(e.target.checked);
                      if (e.target.checked) {
                        setBaseSelected(
                          basesToSelect.map((state) => state.value),
                        );
                      } else {
                        setBaseSelected([]);
                      }
                    }}
                    size="sm"
                    colorScheme="softplan"
                  >
                    Todos
                  </Checkbox>
                </Flex>

                <OwnSelect
                  placeholder="Selecione"
                  options={basesToSelect}
                  isInvalid={!!errors?.states}
                  isMulti
                  value={basesToSelect.filter((s) =>
                    baseSelected.includes(s.value),
                  )}
                  isLoading={isFindingBases}
                  isDisabled={isFindingBases || basesToSelect.length === 0}
                  isClearable
                  onChange={(selected: SelectedValues) => {
                    const value = selected.map((s) => Number(s.value));
                    setBaseSelected(value);
                  }}
                />
                <FormErrorMessage>Base é obrigatória</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors?.integrations}>
                <FormLabel>Integrações</FormLabel>

                <OwnSelect
                  placeholder="Selecione"
                  options={integrationsToSelect}
                  isInvalid={!!errors?.integrations}
                  isMulti
                  value={integrationsToSelect.filter((s) =>
                    integrationSelected.includes(s.value),
                  )}
                  isDisabled={integrationsToSelect.length === 0}
                  isClearable
                  isOptionDisabled={(option) => {
                    if (!hasSelectedExclusiveIntegration) {
                      return false;
                    }

                    if (integrationSelected.includes(option.value)) {
                      return false;
                    }

                    return (
                      (option.value === SIENGE_PLATFORM_INTEGRATION_KEY &&
                        integrationSelected.includes(
                          GESTOR_OBRAS_INTEGRATION_KEY,
                        )) ||
                      (option.value === GESTOR_OBRAS_INTEGRATION_KEY &&
                        integrationSelected.includes(
                          SIENGE_PLATFORM_INTEGRATION_KEY,
                        ))
                    );
                  }}
                  onChange={(selected: SelectedValues) => {
                    const value = selected.map((s) => s.value);
                    setIntegrationSelected(value);
                  }}
                />

                {hasSelectedExclusiveIntegration && (
                  <Text mt={2} fontSize="xs" color="gray.500">
                    {integrationConflictMessage}
                  </Text>
                )}

                <FormErrorMessage>
                  {!!errors?.integrations && integrationConflictMessage}
                </FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors?.numberOfUsers}>
                <FormLabel>Número de usuários</FormLabel>

                <Input
                  type="number"
                  value={numberOfUsers || ''}
                  onChange={(e) => setNumberOfUsers(Number(e.target.value))}
                  ref={numberOfUsersRef}
                  bg="white"
                  borderColor="gray.300"
                  size="md"
                />

                <FormErrorMessage>
                  {!!errors?.numberOfUsers &&
                    'Número de usuários é obrigatório'}
                </FormErrorMessage>
              </FormControl>
            </Box>

            <Box
              borderLeft={{ base: 'none', lg: '1px solid #E2E8F0' }}
              borderColor="#E2E8F0"
              pl={{ base: 0, lg: 5 }}
            >
              <Heading size="sm" color="gray.700" mb={1}>
                Funcionalidades
              </Heading>

              <Text fontSize="sm" color="gray.500" mb={5}>
                Defina os recursos habilitados para este plano.
              </Text>

              <Stack spacing={3} mt={{ base: 0, lg: 12 }}>
                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    Habilitar CRM/Flow
                  </Text>
                  <Switch
                    size="md"
                    colorScheme="softplan"
                    isChecked={useFlow}
                    onChange={(e) => setUseFlow(e.target.checked)}
                  />
                </Flex>

                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    Habilitar Orçamento Paramétrico
                  </Text>
                  <Switch
                    size="md"
                    colorScheme="softplan"
                    isChecked={useParametricBudget}
                    onChange={(e) => setUseParametricBudget(e.target.checked)}
                  />
                </Flex>

                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    Habilitar Dashboard do Flow
                  </Text>
                  <Switch
                    size="md"
                    colorScheme="softplan"
                    isChecked={useFlowDashboard}
                    onChange={(e) => setUseFlowDashboard(e.target.checked)}
                  />
                </Flex>

                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                >
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    Habilitar Loja de Templates
                  </Text>
                  <Switch
                    size="md"
                    colorScheme="softplan"
                    isChecked={useTemplate}
                    onChange={(e) => setUseTemplate(e.target.checked)}
                  />
                </Flex>

                {shouldShowUseOpportunitiesFeature && (
                  <Flex
                    align="center"
                    justify="space-between"
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                  >
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                      Habilitar Oportunidades
                    </Text>
                    <Switch
                      size="md"
                      colorScheme="softplan"
                      isChecked={useOpportunities}
                      onChange={(e) => setUseOpportunities(e.target.checked)}
                    />
                  </Flex>
                )}
              </Stack>

              <Divider my={5} />

              <Text fontSize="xs" color="gray.500">
                As funcionalidades podem ser ajustadas a qualquer momento na
                edição do plano.
              </Text>
            </Box>
          </Grid>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100">
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleConfirm}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalPlanAdd;
